import { action, computed } from 'mobx'

import { RecipientType } from '~/client/graph'
import {
  DeleteManySitePermitAssignmentsDocument,
  SaveManySitePermitAssignmentsDocument,
} from '~/client/graph/operations/generated/SitePermitAssignment.generated'

import Assignment from '../../models/Assignment/Assignment'
import BaseAssignmentsStore from '../BaseAssignments.store'
import EventsStore from '../EventStore/Events.store'
import TagsStore from './Tags.store'

export default class WorkflowAssignmentsStore extends BaseAssignmentsStore {
  public constructor(eventsStore: EventsStore, tagsStore: TagsStore) {
    super(
      eventsStore,
      tagsStore,
      SaveManySitePermitAssignmentsDocument,
      DeleteManySitePermitAssignmentsDocument,
    )
  }

  public get byId() {
    return this.eventsStore.appState.workflowAssignments
  }

  @computed
  public get workflowIdToAssignedUserIdsMap(): {
    [permitId: string]: string[]
  } {
    const map: { [permitId: string]: string[] } = {}

    this.list.forEach(assignment => {
      const permitId = assignment.entityId
      if (!permitId) {
        return
      }

      const usersSet = new Set<string>()

      assignment.recipients?.forEach(recipient => {
        const ids = recipient?.ids || []

        if (recipient?.type === RecipientType.User) {
          ids.forEach(id => usersSet.add(id))
        } else {
          this.tagsStore
            .getUsersIdsByTagIds(ids)
            .forEach(id => usersSet.add(id))
        }
      })

      const existing = map[permitId] || []
      map[permitId] = Array.from(new Set<string>([...existing, ...usersSet]))
    })

    return map
  }

  public get permitIdToAssignedUserIdsMap(): { [permitId: string]: string[] } {
    return this.workflowIdToAssignedUserIdsMap
  }

  @action.bound
  public removeUserFromAssignment(permitId: string, userId: string) {
    const existing = this.getAssignmentByEntityId(permitId)
    if (!existing) {
      return
    }
    const recipients = existing.recipients || []
    const userRecipients =
      recipients.find(r => r.type === RecipientType.User) || null

    if (userRecipients) {
      userRecipients.ids = (userRecipients.ids || []).filter(
        id => id !== userId,
      )
      // If empty, drop this recipient entry
      if (!userRecipients.ids.length) {
        existing.recipients = recipients.filter(
          r => r.type !== RecipientType.User,
        )
      }
    }

    if (
      (!existing.recipients || existing.recipients.length === 0) &&
      !existing.isDraft
    ) {
      this.delete([existing.id])
      return
    }

    this.save([existing.getDto()])
    return
  }

  @action.bound
  public addUserToAssignment(
    permitId: string,
    userId: string,
    projectId: string,
  ) {
    const existing = this.getAssignmentByEntityId(permitId)

    // Not assigned -> add current user
    if (existing) {
      let userRecipients =
        existing.recipients?.find(r => r.type === RecipientType.User) || null
      if (!userRecipients) {
        userRecipients = { type: RecipientType.User, ids: [] as string[] }
        existing.recipients = [...(existing.recipients || []), userRecipients]
      }
      const set = new Set<string>(userRecipients.ids || [])
      set.add(userId)
      userRecipients.ids = Array.from(set)
      this.save([existing.getDto()])
      return
    }

    // Create new assignment
    const draft = Assignment.getDraft()
    draft.projectId = projectId
    draft.recipients = [{ type: RecipientType.User, ids: [userId] }]
    this.save([draft.getDto(permitId)])
  }
}
